import type {
	IPageDidMountHandler,
	IPageWillMountHandler,
	ICompsLifeCycle,
	IPropsStore,
	ILogger,
} from '@wix/thunderbolt-symbols'
import type { IFeatureExportsStore } from 'thunderbolt-feature-exports'
import type { InteractionsPageConfig } from './types'
import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	PageFeatureConfigSymbol,
	Props,
	CompsLifeCycleSym,
	LoggerSymbol,
	FeatureExportsSymbol,
	pageIdSym,
} from '@wix/thunderbolt-symbols'
import { HasInteractionsRegistrySym, name } from './symbols'
import type { HasInteractionsRegistry } from './types'
import { TRIGGERS_AND_REACTIONS_NAME } from './constants'

const CLICK_TRIGGER_CLASS_NAME = 'has-click-trigger'

const interactionsFactory = (
	{ interactionsConfig }: InteractionsPageConfig,
	triggersAndReactionsExports: IFeatureExportsStore<typeof TRIGGERS_AND_REACTIONS_NAME>,
	props: IPropsStore,
	compsLifeCycle: ICompsLifeCycle,
	pageIdSymbol: string,
	logger: ILogger,
	hasInteractionsRegistry: HasInteractionsRegistry | null
): IPageWillMountHandler & IPageDidMountHandler => {
	const hasInteractions = interactionsConfig.interactions.length > 0
	if (hasInteractions) {
		hasInteractionsRegistry?.setHasInteractions()
	}

	const getClassNames = (compId: string) => {
		const className = props.get(compId)?.className
		return className ? className.split(' ') : []
	}

	const addClassName = (compId: string, className: string) => {
		const currentClassNames = getClassNames(compId)
		const uniqueClassNames = [...new Set([...currentClassNames, className])]
		props.update({ [compId]: { className: uniqueClassNames.join(' ') } })
	}

	const addClickTriggerClassName = async () => {
		const { interactions } = interactionsConfig

		const clickInteractions = interactions.filter((interaction) => interaction.trigger === 'activate')
		const uniqueCompIdsWithHashTags = [...new Set(clickInteractions.map((interaction) => interaction.key))]
		const compIds = uniqueCompIdsWithHashTags.map((id: string) => id?.replace('#', ''))

		const addClassNameAfterRender = async (compId: string) => {
			try {
				await compsLifeCycle.waitForComponentToRender(compId)
				addClassName(compId, CLICK_TRIGGER_CLASS_NAME)
			} catch (error) {
				logger.captureError(error, {
					tags: { feature: 'interactions', methodName: 'addClickTriggerClassName' },
				})
			}
		}

		await Promise.allSettled(compIds.map((compId) => addClassNameAfterRender(compId)))
	}

	// noop implementation, with the interactions service loaded support for interactions on comps with dynamic children is already implemented
	const observeChildListChange = () => {}

	return {
		name: 'interactions',
		pageWillMount: () => {
			if (pageIdSymbol !== 'masterPage') {
				triggersAndReactionsExports.export({ observeChildListChange })
			} else {
				triggersAndReactionsExports.export({ observeChildListChangeMaster: observeChildListChange })
			}
		},
		pageDidMount: () => {
			addClickTriggerClassName()
		},
	}
}

export const Interactions = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureExportsSymbol, TRIGGERS_AND_REACTIONS_NAME),
		Props,
		CompsLifeCycleSym,
		pageIdSym,
		LoggerSymbol,
		optional(HasInteractionsRegistrySym),
	],
	interactionsFactory
)
